// Framework-agnostic jsPDF drawing helpers for the referral PDF.
// Everything Angular-specific (filters, services, constants) is injected by the
// thin `components` factory in services/pdfComponents.js.
const moment = require('moment');

// jsPDF 3 dropped setFontType(style); setFont(family, style) is the replacement,
// and the family has to be repeated on every call.
const FONT_FAMILY = 'helvetica';

module.exports = function createPdfComponents(deps) {
  const { fileTypes, imageUtils } = deps;

  const utils = {
    extractFileName: deps.extractFileName,
    formatDate: deps.formatDate,
    findIcon: (attachment) => {
      let type = attachment.metadata.attach_file_name.slice(attachment.metadata.attach_file_name.lastIndexOf('.') + 1);
      type = type.toLowerCase();
      type = fileTypes.aliases[type] || type;
      return fileTypes.icons[type] || fileTypes.icons['default'];
    }
  };

  const components = {
    fitString: (pdf, styles, str, expectedLengthMm) => {
      let res = str;
      let l = str.length;
      const trail = '...';
      while (true) {
        const width = pdf.getStringUnitWidth(res) * styles.fontSizeMm;
        if (width <= expectedLengthMm) return res;
        if (res === trail) return '~';
        l -= 1;
        res = str.substring(0, l) + trail;
      }
    },

    addPage: (pdf, styles, data) => {
      pdf.addPage();
      return components.appendHeader(pdf, styles, data.referralDate);
    },

    addPageIfNeeded: (pdf, styles, caret, data, expectedHeight) => {
      if (caret + expectedHeight > styles.pageSizes.height - styles.pagePaddings.y) {
        return components.addPage(pdf, styles, data);
      }
      return caret;
    },

    appendHeader: (pdf, styles, referralDate) => {
      const { pagePaddings, fontSize, fontColor, headerColor, headerFontSize, headerFontSizeMm, headerFontColor, headerHeight, headerDatePaddings, blocksPadding, pageSizes } = styles;

      // note: appendHeader() resets draw, fill and font styles! No way to "remember" arbitrary styles and restore them. Therefore, best guess restoration below
      pdf.setFillColor(headerColor.r, headerColor.g, headerColor.b);
      pdf.rect(0, 0, pageSizes.width, headerHeight, 'F');

      var y = pagePaddings.y + headerFontSizeMm; // because jsPDF draws text "up" from the specified Y

      pdf.setTextColor(headerFontColor.r, headerFontColor.g, headerFontColor.b);
      pdf.setFontSize(headerFontSize);
      var strBold = 'Dental';
      pdf.setFont(FONT_FAMILY, 'bold');
      var width = pdf.getStringUnitWidth(strBold) * headerFontSizeMm;
      pdf.text(strBold, pagePaddings.x, y);

      var strNormal = 'CareLinks.com';
      pdf.setFont(FONT_FAMILY, 'normal');
      pdf.text(strNormal, pagePaddings.x + width, y);

      pdf.setFontSize(fontSize);
      var dateReferred = 'Referred: ' + new Date(referralDate).toLocaleString();
      var dateGenerated = 'Generated: ' + new Date().toLocaleString();
      pdf.text(dateReferred, pagePaddings.x + headerDatePaddings.x, y + headerDatePaddings.y);
      pdf.text(dateGenerated, pagePaddings.x + headerDatePaddings.x, y);

      // best guess styles restoration :/
      pdf.setFillColor(255, 255, 255);
      pdf.setTextColor(fontColor.r, fontColor.g, fontColor.b);

      return headerHeight + blocksPadding;
    },

    appendAllNotes: (pdf, styles, caret, data, notes) => {
      if (notes && notes.length) {
        const filteredNotes = notes.filter(note => note.user);
        if (filteredNotes.length) {
          pdf.setFont(FONT_FAMILY, 'normal');
          caret = filteredNotes.reduce((acc, note) => components.appendNote(pdf, styles, acc, data, note), caret);
          caret += styles.blocksPadding;
          caret = components.appendLineSeparator(pdf, styles, caret);
        }
      }
      return caret;
    },

    appendNote: (pdf, styles, caret, data, note) => {
      const { pagePaddings, fontSizeMm } = styles;

      caret += fontSizeMm;
      pdf.setFont(FONT_FAMILY, 'bold');
      pdf.text(`${note.user.first_name} ${note.user.last_name} ${moment(note.created_at).format("MMM D, YYYY h:mm")}`, pagePaddings.x, caret);
      pdf.setFont(FONT_FAMILY, 'normal');
      const noteLines = pdf.splitTextToSize(note.message, styles.fullSizeColWidth);
      const noteHeight = noteLines.length * fontSizeMm;
      caret = components.addPageIfNeeded(pdf, styles, caret, data, noteHeight);
      caret += fontSizeMm;
      pdf.text(noteLines, pagePaddings.x, caret);
      caret += noteHeight;
      return caret;
    },

    setAuxiliaryFontStyles: (pdf, styles) => {
      const { auxiliaryFontSize, auxiliaryFontColor } = styles;

      pdf.setFontSize(auxiliaryFontSize);
      pdf.setTextColor(auxiliaryFontColor.r, auxiliaryFontColor.g, auxiliaryFontColor.b);
      pdf.setFont(FONT_FAMILY, 'italic');
    },

    setNormalFontStyles: (pdf, styles) => {
      const { fontSize, fontColor } = styles;

      pdf.setFontSize(fontSize);
      pdf.setTextColor(fontColor.r, fontColor.g, fontColor.b);
      pdf.setFont(FONT_FAMILY, 'normal');
    },

    appendAllImages: (pdf, styles, caret, data, attachments) => {
      if (attachments && attachments.length > 0) {
        components.setAuxiliaryFontStyles(pdf, styles);
        caret += styles.auxiliaryFontSizeMm; // assuming we're not at EOP yet and can output one line
        const text = 'See full-size attachments on subsequent pages. Also, visit www.dentalcarelinks.com for more information';
        pdf.text(pdf.splitTextToSize(text, styles.fullSizeColWidth), styles.pagePaddings.x, caret);

        caret = attachments
          .filter(attachment => attachment && attachment.image)
          .reduce((acc, imageAtt) => components.appendImage(pdf, styles, imageAtt.image, imageAtt.metadata, data, acc), caret);
      }
      return caret;
    },

    appendImage: (pdf, styles, image, metadata, data, caret) => {
      const { fullSizeColWidth, pagePaddings, blocksPadding, pageSizes, fontSizeMm, headerHeight } = styles;

      caret = components.addPage(pdf, styles, data); // includes addHeader() resetting styles

      var outputDimensions = imageUtils.resizeImage(
        { width: image.width, height: image.height },
        { width: fullSizeColWidth, height: pageSizes.height - headerHeight - 2 * blocksPadding - fontSizeMm - pagePaddings.y }
      );

      pdf.addImage(image, 'JPEG', pagePaddings.x, caret, outputDimensions.width, outputDimensions.height);
      caret += outputDimensions.height + blocksPadding;

      components.setNormalFontStyles(pdf, styles);

      caret += styles.fontSizeMm;
      pdf.text(utils.extractFileName(metadata.attach_file_name) +
        (metadata.last_modified ? ' - Created: ' + utils.formatDate(metadata.last_modified) : '') +
        (metadata.attach_updated_at ? ' - Uploaded: ' + utils.formatDate(metadata.attach_updated_at) : '') +
        (metadata.author ? ' -  Added by: ' + metadata.author.first_name + ' ' + metadata.author.last_name : ''), pagePaddings.x, caret);

      caret += blocksPadding;

      return caret;
    },

    appendThumbnails: (pdf, styles, caret, data, attachments) => {
      if (attachments && attachments.length > 0) {
        const { pagePaddings, fontSize, fontColor, thumbnailSquareSize, thumbnailTextPaddingX, blocksPadding } = styles;

        pdf.setFontSize(fontSize);
        pdf.setTextColor(fontColor.r, fontColor.g, fontColor.b);
        pdf.setFont(FONT_FAMILY, 'normal');

        // that's in case we're too close to page bottom already
        caret = components.addPageIfNeeded(pdf, styles, caret, data, thumbnailSquareSize); // may include addHeader() resetting styles, but we have those default styles here anyway

        var xCaret = pagePaddings.x;
        var remainingWidth = styles.halfSizeColWidth - thumbnailSquareSize - thumbnailTextPaddingX;
        for (var j = 0; j < attachments.length; j++) {
          const attachment = attachments[j];
          if (attachment) {
            if (!attachment.image) {
              attachment.icon = utils.findIcon(attachment);
            }
            var image = attachment.image || attachment.icon;
            var metadata = attachment.metadata;

            // TODO: extract to a separate function
            var thumbDimensions = imageUtils.resizeImage({
              width: image.width ? image.width : thumbnailSquareSize,
              height: image.height ? image.height : thumbnailSquareSize
            }, { width: thumbnailSquareSize, height: thumbnailSquareSize });

            var currentX = xCaret + thumbnailSquareSize + thumbnailTextPaddingX;
            var currentY = caret;
            pdf.addImage(image, 'JPEG', xCaret, currentY, thumbDimensions.width, thumbDimensions.height);
            const fontSizeMm = styles.fontSizeMm;
            currentY += fontSizeMm;
            pdf.text(components.fitString(pdf, styles, utils.extractFileName(metadata.attach_file_name), remainingWidth), currentX, currentY);
            if (metadata.last_modified) {
              currentY += fontSizeMm;
              pdf.text('Created:', currentX, currentY);
              currentY += fontSizeMm;
              pdf.text(utils.formatDate(metadata.last_modified), currentX, currentY);
            }
            if (metadata.attach_updated_at) {
              currentY += fontSizeMm;
              pdf.text('Uploaded:', currentX, currentY);
              currentY += fontSizeMm;
              pdf.text(utils.formatDate(metadata.attach_updated_at), currentX, currentY);
            }
            if (metadata.author) {
              currentY += fontSizeMm;
              pdf.text('Added by:', currentX, currentY);
              currentY += fontSizeMm;
              pdf.text(components.fitString(pdf, styles, metadata.author.first_name + ' ' + metadata.author.last_name, remainingWidth), currentX, currentY);
            }

            if (currentX < styles.secondColX) {
              // switch to second column
              xCaret = styles.secondColX;
            } else {
              // switch to first column and next row
              xCaret = pagePaddings.x;
              caret += thumbnailSquareSize + blocksPadding;
              // may include addHeader() resetting styles
              caret = components.addPageIfNeeded(pdf, styles, caret, data, thumbnailSquareSize);
            }
          }
        }
        if (pagePaddings.x < currentX && currentX < styles.secondColX) {
          // last thumbnail is in first column, caret needs to be increased
          caret += thumbnailSquareSize + blocksPadding;
        }
        caret = components.appendLineSeparator(pdf, styles, caret);
      }
      return caret;
    },

    appendPractice: (pdf, styles, x, caret, practiceData) => {
      const { fontSizeMm, halfSizeColWidth, addressPaddingX, logo, pixelsToMm } = styles;

      if (practiceData.logoImg) {
        pdf.addImage(practiceData.logoImg, 'JPEG', x, caret);
        const height = pixelsToMm(practiceData.logoImg.height);
        caret += height + logo.padding;
      }

      pdf.setFont(FONT_FAMILY, 'bold');
      caret += fontSizeMm;
      pdf.text(practiceData.blockTitle, x, caret);
      caret += fontSizeMm;
      pdf.setFont(FONT_FAMILY, 'normal');

      var paddedX = x + addressPaddingX;

      pdf.text(components.fitString(pdf, styles, practiceData.doctorName, halfSizeColWidth - addressPaddingX), paddedX, caret);
      caret += fontSizeMm;
      pdf.text(components.fitString(pdf, styles, practiceData.practiceName, halfSizeColWidth - addressPaddingX), paddedX, caret);
      caret += fontSizeMm;
      pdf.text(components.fitString(pdf, styles, practiceData.phone, halfSizeColWidth - addressPaddingX), paddedX, caret);
      caret += fontSizeMm;
      pdf.text(components.fitString(pdf, styles, practiceData.addressStreet, halfSizeColWidth - addressPaddingX), paddedX, caret);
      caret += fontSizeMm;
      pdf.text(components.fitString(pdf, styles, practiceData.addressCity, halfSizeColWidth - addressPaddingX), paddedX, caret);
      caret += fontSizeMm;
      if (practiceData.website) {
        pdf.text(components.fitString(pdf, styles, practiceData.website, halfSizeColWidth - addressPaddingX), paddedX, caret);
        caret += fontSizeMm;
      }
      return caret;
    },

    verticalSeparator: (pdf, pageSizes, startY, endY) => {
      pdf.line(pageSizes.centerHorizontal, startY, pageSizes.centerHorizontal, endY);
    },

    appendPractices: (pdf, styles, caret, originalPracticeData, destinationPracticeData) => {
      const { pageSizes, blocksPadding, pagePaddings, secondColX, fontSize, fontSizeMm } = styles;
      pdf.setFontSize(fontSize);
      const startY = caret;
      caret += fontSizeMm;
      var caretLeft = components.appendPractice(pdf, styles, pagePaddings.x, caret, originalPracticeData);
      var caretRight = components.appendPractice(pdf, styles, secondColX, caret, destinationPracticeData);
      caret = Math.max(caretLeft, caretRight);

      components.verticalSeparator(pdf, pageSizes, startY, caret - blocksPadding);
      return caret;
    },

    appendPatientTextData: (pdf, styles, caret, patientData) => {
      const { pagePaddings, fontSizeMm, halfSizeColWidth, blocksPadding, addressPaddingX } = styles;

      caret += fontSizeMm;
      pdf.setFont(FONT_FAMILY, 'bold');
      pdf.text(components.fitString(pdf, styles, patientData.name + ' (' + utils.formatDate(patientData.birthday) + ')', styles.fullSizeColWidth), pagePaddings.x, caret);
      pdf.setFont(FONT_FAMILY, 'normal');
      caret += fontSizeMm;
      pdf.text(components.fitString(pdf, styles, patientData.email, halfSizeColWidth - addressPaddingX), pagePaddings.x, caret);
      caret += fontSizeMm;
      pdf.text(components.fitString(pdf, styles, patientData.phone, halfSizeColWidth - addressPaddingX), pagePaddings.x, caret);
      caret += fontSizeMm;
      return caret + blocksPadding;
    },

    appendProcedureData: (pdf, styles, caret, procedureData) => {
      const { pagePaddings, fontSizeMm, blocksPadding } = styles;
      caret += fontSizeMm;
      pdf.setFont(FONT_FAMILY, 'bold');
      pdf.text(procedureData.practiceType, pagePaddings.x, caret);
      caret += fontSizeMm;
      pdf.text(procedureData.name, pagePaddings.x, caret);
      if (procedureData.teeth) {
        var caption;
        var teethArray = procedureData.teeth.split('+');
        if (teethArray && teethArray.length > 0) {
          caret += fontSizeMm;
          if (teethArray.length == 1) {
            caption = 'Tooth ';
          } else {
            caption = 'Teeth ';
          }
          pdf.text(caption + teethArray.sort(function (a, b) {
            return a - b;
          }).join(', '), pagePaddings.x, caret);
        }
      }
      return caret + blocksPadding;
    },

    appendLineSeparator: (pdf, styles, caret) => {
      pdf.line(styles.pagePaddings.x, caret, styles.pagePaddings.x + styles.fullSizeColWidth, caret);
      return caret + styles.blocksPadding;
    },

    leftHorizontalSeparator: (pdf, styles, caret) => {
      const { pagePaddings, pageSizes } = styles;
      pdf.line(pagePaddings.x, caret, pageSizes.centerHorizontal, caret);
      return caret + styles.blocksPadding;
    },

    appendQRCode: (pdf, styles, startRowY, destinationPracticeData) => {
      const { pixelsToMm, blocksPadding, secondColX } = styles;

      let caret = startRowY;

      if (destinationPracticeData.qrCode) {
        const sideLength = pixelsToMm(destinationPracticeData.qrCode.width);
        pdf.addImage(destinationPracticeData.qrCode, 'JPEG', secondColX, caret);
        pdf.text('Scan to visit the practice\nand schedule an appointment', secondColX + sideLength, caret + blocksPadding + 2);

        caret += sideLength;
      }

      return caret;
    },

    appendBottomText: (pdf, styles, caret) => {
      const { pagePaddings, auxiliaryFontSize, auxiliaryFontSizeMm, auxiliaryFontColor, pageSizes } = styles;

      var str = 'Visit www.dentalcarelinks.com to see referral documents';
      pdf.setTextColor(auxiliaryFontColor.r, auxiliaryFontColor.g, auxiliaryFontColor.b);
      pdf.setFont(FONT_FAMILY, 'italic');
      pdf.setFontSize(auxiliaryFontSize);
      caret += auxiliaryFontSizeMm;

      var width = pdf.getStringUnitWidth(str) * auxiliaryFontSizeMm;
      pdf.text(str, pageSizes.width - pagePaddings.x - width, caret);
    }
  };

  return components;
};
